import { useMutation } from '@apollo/client';
import {
  languageByOrganizationUserLanguage,
  organizationUserLanguageByLanguage,
} from '@constants/organizationUserLanguage';
import type { LanguageCodes } from '@onevesthq/ov-enums';
import {
  languageCodeToLanguageMap,
  languageToLanguageCodeMap,
} from '@onevesthq/ov-translations';
import { UserContext } from '@providers/userContextProvider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from 'hephaestus/web';
import { Check as CheckIcon, Close as CloseIcon } from 'hephaestus/webIcons';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { UPDATE_ORGANIZATION_USER_LANGUAGE } from './languageSelectionModal.query';

export type LanguageSelectionModalProp = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const LanguageSelectionModal = ({
  open,
  setOpen,
}: LanguageSelectionModalProp) => {
  const { t } = useTranslation(['components', 'shared']);
  const theme = useTheme();
  const { setUserContext } = useContext(UserContext);
  const { language, setLanguage, applicableLanguages } = useLocalization();

  const [updateOrganizationUserLanguage, { loading }] = useMutation(
    UPDATE_ORGANIZATION_USER_LANGUAGE,
  );

  const close = () => setOpen(false);

  const selectLanguage = (code: LanguageCodes) => {
    void updateOrganizationUserLanguage({
      variables: {
        input: {
          language:
            organizationUserLanguageByLanguage[languageCodeToLanguageMap[code]],
        },
      },
      onCompleted: (data) => {
        const saved =
          data.updateOrganizationUserProfile.organizationUser?.language;
        if (saved) {
          const savedLanguage = languageByOrganizationUserLanguage[saved];
          setUserContext((prev) => ({
            ...prev,
            language: savedLanguage,
          }));
          setLanguage(languageToLanguageCodeMap[savedLanguage]);
        }
        close();
      },
    });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
      <DialogTitle>
        {t('languageSelection.language')}
        <IconButton aria-label={t('shared:close')} onClick={close}>
          <CloseIcon fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ '&&': { p: 0 } }}>
        {applicableLanguages.map((code) => {
          const isActive = language === code;

          return (
            <Button
              disabled={loading}
              key={code}
              fullWidth
              variant='text'
              aria-pressed={isActive}
              onClick={() => selectLanguage(code)}
              endIcon={isActive ? <CheckIcon aria-hidden /> : undefined}
              sx={{
                justifyContent: 'space-between',
                borderRadius: 0,
                height: 'auto',
                py: 2,
                px: 3,
                fontSize: '16px',
                backgroundColor: isActive
                  ? theme.color.primaryContainer
                  : undefined,
                '&:hover': {
                  backgroundColor: theme.color.surfaceContainer,
                },
              }}
            >
              {t(`languageSelection.languageList.${code}`)}
            </Button>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};
